import { hasStarted } from '@utilities';

import { CREDIT_TYPES } from './constants';

/**
 * Transforms client credit information into a more user-friendly format
 * @param {Object} response - The raw API response
 * @returns {Array} Transformed credit information with labels
 */
export const transformClientCredits = (response) => {
  const holdForFutureProjects = response?.on_hold_credits || 0;
  const inFlightCredits = response?.in_flight_credits || 0;
  const totalCredits = response?.total_credits || 0;

  const availableCredits = totalCredits - inFlightCredits;

  return [
    {
      isDisplayed: true,
      label: 'Available Credits',
      tooltip: 'Remaining credits - in flight credits',
      type: CREDIT_TYPES.AVAILABLE,
      value: availableCredits,
    },
    {
      isDisplayed: true,
      isUpdateable: true,
      label: 'Hold for Future Projects',
      type: CREDIT_TYPES.HOLD_FOR_FUTURE,
      value: holdForFutureProjects,
    },
    {
      isDisplayed: false,
      label: 'In Flight Credits',
      type: CREDIT_TYPES.IN_FLIGHT,
      value: inFlightCredits,
    },
  ];
};

/**
 * Column definitions for the user credits table
 */
export const USER_CREDITS_ITEM_COLUMNS = [
  {
    accessor: 'display',
    disableSortBy: true,
    Footer: 'Total',
    Header: 'Name',
    width: 30,
  },
  {
    accessor: 'neg_diff',
    disableSortBy: true,
    Header: 'Dif',
    id: 'neg_diff',
    width: 7,
  },
  {
    accessor: 'allocatedCredits',
    format: '0,0.0',
    Header: 'Allocated Credits',
    width: 18,
  },
  {
    accessor: 'pos_diff',
    disableSortBy: true,
    Header: 'Dif',
    id: 'pos_diff',
    width: 7,
  },
  {
    accessor: 'spacer',
    Cell: () => null,
    disableSortBy: true,
    Header: '',
    width: 30,
  },
];

/**
 * Transforms client credits data to match the format expected by the API
 * @param {Object} data - The client credits data
 * @returns {Object} Transformed client credits data
 */
export const transformClientCreditsforApi = (data) => {
  return {
    in_flight_credits: data.inFlightCredits || 0,
    on_hold_credits: data.holdForFutureProjects || 0,
    total_credits: data.availableCredits || 0,
  };
};

/**
 * Transforms user credits data into a more user-friendly format
 * @param {Object} response - The raw API response
 * @returns {Array} Transformed user credits data
 */
export const transformUserCreditsData = (response) => {
  return response?.results
    ?.map((user) => {
      const allocated = user?.survey_credits ?? 0;
      const starting = user?.survey_credits ?? 0;
      const diff = allocated - starting;

      return {
        allocatedCredits: allocated,
        diff,
        display: `${user?.first_name} ${user?.last_name}`,
        id: user?.id,
        neg_diff: diff < 0 ? diff : undefined,
        pos_diff: diff > 0 ? diff : undefined,
        startingCredits: starting,
      };
    })
    .sort((a, b) => (b.allocatedCredits ?? 0) - (a.allocatedCredits ?? 0));
};

/**
 * Transforms client subscription data into a user-friendly format while preserving
 * @param {Array<Object>} response
 * @param {number}   [response.id]
 * @param {string}   [response.type]
 * @param {string}   [response.start_date]
 * @param {string}   [response.end_date]
 * @param {number}   [response.completes_per_period]
 * @param {number}   [response.completes_total]
 * @param {number}   [response.completes_used]
 * @param {number}   [response.monthly_completes_total]
 * @param {number}   [response.monthly_completes_used]
 * @param {string}   [response.monthly_end_date]
 * @param {string}   [response.period_end_date]
 * @param {Array<Object>} [response.subscription_periods]
 * @param {number}   [response.survey_creators_count]
 * @param {number}   [response.survey_viewers_count]
 * @returns {Array<Object>} Array of transformed subscription objects.
 */
export const transformClientSubscriptionData = (response) => {
  if (!Array.isArray(response)) return [];

  return response.map((subscription) => {
    const periods = subscription.subscription_periods || [];
    const currentPeriodObj = periods.find((p) => p.current === true);

    return {
      completesPerPeriod: subscription.completes_per_period,
      completesTotal: subscription.completes_total,
      completesUsed: subscription.completes_used,
      currentPeriod: currentPeriodObj
        ? transformClientSubscriptionPeriod(currentPeriodObj)
        : undefined,
      endDate: subscription.end_date,
      hasFuturePeriods: periods.some(
        (period) => !period.current && new Date(period.start_date) > new Date()
      ),
      hasStarted: hasStarted(subscription?.start_date),
      id: subscription.id,
      monthlyCompletesTotal: subscription.monthly_completes_total,
      monthlyCompletesUsed: subscription.monthly_completes_used,
      monthlyEndDate: subscription.monthly_end_date,
      periodEndDate: subscription.period_end_date,
      periodType: subscription.period_type,
      startDate: subscription.start_date,
      subscriptionPeriods: periods.map(transformClientSubscriptionPeriod),
      surveyCreatorsCount: subscription.survey_creators_count,
      surveyViewersCount: subscription.survey_viewers_count,
      type: subscription.type,
    };
  });
};

/**
 * Transforms a client subscription period into a user-friendly format
 * @param {Object} period - The raw subscription period object
 * @returns {Object} Transformed subscription period object
 */
const transformClientSubscriptionPeriod = (period) => ({
  allowedCompletes: period.allowed_completes,
  createdAt: period.created_at,
  endDate: period.end_date,
  periodType: period.period_type,
  remainingCompletes: period.remaining_completes,
  startDate: period.start_date,
  totalCompletes: period.total_completes,
  updatedAt: period.updated_at,
  usedCompletes: period.used_completes,
});

/**
 * Transforms user credits data for API submission
 * @param {Array} data - Array of user credit data
 * @returns {Array} Transformed data ready for API submission
 */
export const transformUserCreditsForApi = (data) => {
  return data.map((user) => ({
    id: user.id,
    survey_credits: user.allocatedCredits,
  }));
};

/** Transforms user subscription data into a more user-friendly format
 * @param {Object} response - The raw API response
 * @param {number}   [response.count]
 * @param {string}   [response.next]
 * @param {string}   [response.previous]
 * @param {Array<Object>} [response.results] - Array of user subscription objects
 * @param {number}   [response.results.id]
 * @param {string}   [response.results.first_name]
 * @param {string}   [response.results.last_name]
 * @param {boolean}  [response.results.active]
 * @param {number}   [response.results.period_completes_used]
 * @param {number}   [response.results.previous_period_completes_used]
 * @param {string}   [response.results.type]
 * @returns {Object} Transformed user subscription data
 */
export const transformUserSubscriptionData = (response) => {
  return {
    count: response?.count ?? 0,
    next: response?.next ?? null,
    previous: response?.previous ?? null,
    results: response?.results?.map((user) => {
      return {
        firstName: user?.first_name ?? '',
        id: user?.id,
        isSubscribed: user?.active ?? false,
        lastName: user?.last_name ?? '',
        periodCompletesUsed: user?.period_completes_used ?? 0,
        previousPeriodCompletesUsed: user?.previous_period_completes_used ?? 0,
        type: user?.type,
      };
    }),
  };
};
